import WarningIcon from '@mui/icons-material/Warning';
import React, { useState } from 'react';

// Components
import KOGCollapse from '@/main/components/shared/kogCollapse/kogCollapse';
import KOGImage from '@/main/components/shared/kogImage/kogImage';
import QuoteProcurementPartInfo, { getTooltipFormatted } from '@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo';
import { SpinnerLoading } from '@/main/components/shared/styles';

// Utils
import { IMAGE_DEFAULT } from '@/main/services/constants';
import { getTranslateByKey, isNullUndefined, isShowControlByElementId } from '@/main/services/utils';

// Models
import { quoteApi } from '@/main/api';
import {
  QuoteNoSupplierAssignedItemResponse,
  QuoteNoSupplierAssignedResponse,
  QuoteSelectSuppliersDataResponseItems
} from '@/main/models/response';
import { Controls } from '@/main/services/constants/constantsControl';
import { formatCurrency } from '@/main/services/utilsNumber';

// Styles/Components Local
import ButtonDeletePart from './components/buttonDeletePart';
import ButtonRequotePart from './components/buttonRequotePart';
import RemainingTimeShippingTable from './components/remainingTimeShippingTable/remainingTimeShippingTable';
import RowSupplierAssigned from './components/rowSupplierAssigned/rowSupplierAssigned';
import * as S from './styles';
import { AddCartType, RefreshQuoteByGetItemsType } from './components/types';
import { ProcurementStatusEnum } from '@/main/services/enum/StatusProcurement';
import { CartState } from '@/main/store/cart';

interface DetailsCartSupplierUnansweredProps {
  cart: QuoteNoSupplierAssignedResponse | null;
  part: QuoteNoSupplierAssignedItemResponse;
  loadCart: (guidQuote: string) => void;
  idProcurementStatus: number;
  integration: boolean;
  guidQuote: string;
  showButtonRequoteBulkPart: boolean;
  showButtonRemoveBulkPart: boolean;
  isQuoteBlocked: boolean;
  isPaymentPending: boolean;
  dataCart: CartState;
  addCart: AddCartType;
  refreshQuoteByGetItems: RefreshQuoteByGetItemsType;
}
function DetailsCartSupplierUnanswered({
  cart: cartProps,
  part,
  addCart,
  dataCart,
  guidQuote,
  isQuoteBlocked,
  integration,
  idProcurementStatus,
  isPaymentPending,
  showButtonRequoteBulkPart,
  showButtonRemoveBulkPart,
  refreshQuoteByGetItems
}: DetailsCartSupplierUnansweredProps) {
  const cart = cartProps as QuoteNoSupplierAssignedResponse;
  const showPrices = isShowControlByElementId(Controls.IdShowPrices);
  const [isLoadingParts, setisLoadingParts] = useState(false);
  const [dataParts, setDataParts] = useState<QuoteSelectSuppliersDataResponseItems | null>(null)

  function fetchPartsAssigned(isCollapsed: boolean, partAssigned: QuoteNoSupplierAssignedItemResponse) {
    if (isLoadingParts || !isCollapsed) return;
    if (!isNullUndefined(dataParts)) return;
    setisLoadingParts(true)

    quoteApi
      .getSupplierItems(partAssigned.guidQuoteItem)
      .then((response) => {
        const { data: { result } } = response;
        setDataParts(result)
      })
      .catch(() => { })
      .finally(() => {
        setisLoadingParts(false)
      })
  }

  const showButtonRemovePart = () => showButtonRemoveBulkPart
  const showButtonRequotePart = () => showButtonRequoteBulkPart;
  const isQuoteOpen = idProcurementStatus === ProcurementStatusEnum.Open;
  const isQuoteCanceled = idProcurementStatus === ProcurementStatusEnum.Cancelled;
  const isQuoteManual =
    idProcurementStatus === ProcurementStatusEnum.Manual ||
    idProcurementStatus === ProcurementStatusEnum.WaitingRelease;

  return (
    <KOGCollapse
      key={part.guidQuoteItem}
      customIcon={isLoadingParts ? <SpinnerLoading noMargin size='xsmall' /> : undefined}
      onCustomCollapse={(isCollapsed) => {
        fetchPartsAssigned(isCollapsed, part)
      }}
      titleTooltipIcon={{
        showLess: getTranslateByKey('information.showLess'),
        showMore: getTranslateByKey('information.clicktoseetheofers')
      }}
      header={
        <S.BodyBox key={part.guidQuoteItem} style={{ position: 'initial' }}>
          <S.ContainerPart>
            <KOGImage width={80} height={80} src={IMAGE_DEFAULT} />
            <S.PartInfo
              title={getTooltipFormatted(part.quoteProcurementPartInfo)}>
              <QuoteProcurementPartInfo
                data={part.quoteProcurementPartInfo}
                renderType="column"
                showIconNotes
              />
            </S.PartInfo>
            {(showPrices && !isNullUndefined(part.priceCap)) && (
              <S.TextPriceCap>
                {`${getTranslateByKey('information.priceCap')}: ${formatCurrency(part.priceCap)}`}
              </S.TextPriceCap>
            )}
          </S.ContainerPart>
          <S.AreaButtonAbsolute>
            {showButtonRequotePart() && (
              <ButtonRequotePart
                type='unaswered'
                dataCart={dataCart}
                refreshQuoteByGetItems={refreshQuoteByGetItems}
                isQuoteBlocked={isQuoteBlocked}
                isQuoteCanceled={isQuoteCanceled}
                part={{
                  guidQuoteItem: part.guidQuoteItem,
                  idPartType: part.idPartType,
                  replacePartType: false
                }}
                idButtonRequotePart={`id-createOrder-button-requoteProduct-${cart.showDeadlineAnswer}`}
                isRequotePartCanceled={false}
              />
            )}
            {showButtonRemovePart() && (
              <ButtonDeletePart
                type='unaswered'
                dataCart={dataCart}
                refreshQuoteByGetItems={refreshQuoteByGetItems}
                guidQuote={guidQuote}
                isQuoteBlocked={isQuoteBlocked}
                isQuoteCanceled={isQuoteCanceled}
                isPaymentPending={isPaymentPending}
                guidQuoteItem={part.guidQuoteItem}
                idButtonDeletePart={`id-createOrder-button-deleteProduct-${cart.showDeadlineAnswer}`}
              />
            )}
          </S.AreaButtonAbsolute>
        </S.BodyBox>
      }
      content={
        <RowSupplierAssigned
          addCart={addCart}
          dataCart={dataCart}
          integration={integration}
          isQuoteManual={isQuoteManual}
          isQuoteOpen={isQuoteOpen}
          isWaitingRelease={
            idProcurementStatus === ProcurementStatusEnum.WaitingRelease
          }
          dataParts={dataParts}
          isLoading={isLoadingParts}
          externalPartId={part.externalPartId}
          guidPart={part.guidPart}
          guidQuote={String(guidQuote)}
        />
      }
    />)
}

interface BoxCartUnansweredItemsProps extends Omit<DetailsCartSupplierUnansweredProps, 'part'> {
}
function BoxCartUnansweredItems(props: BoxCartUnansweredItemsProps) {
  if (isNullUndefined(props.cart) || props.cart.parts.length === 0) return null

  const isQuoteOpen = props.idProcurementStatus === ProcurementStatusEnum.Open;
  const showButtonRequoteBulkPart = !isQuoteOpen && props.showButtonRequoteBulkPart;

  return (
    <S.ContainerBox>
      <S.HeaderBox>
        <S.ContainerTitleBox>
          <S.TitleBox>
            {getTranslateByKey("information.shoppingTable")} -
          </S.TitleBox>
          <S.TitleBoxWarning>
            {props.cart.label}
            <WarningIcon
              fontSize='small'
              color='error'
            />
          </S.TitleBoxWarning>
          <RemainingTimeShippingTable
            dataCart={props.dataCart}
            cart={props.cart}
            loadCart={props.loadCart}
            guidQuote={props.guidQuote}
            type='unaswered'
          />
        </S.ContainerTitleBox>
      </S.HeaderBox>
      <S.ContentBox>
        {props.cart.parts.map(
          (part, index) => (
            <DetailsCartSupplierUnanswered
              key={part.guidQuoteItem}
              {...props}
              part={part}
              showButtonRemoveBulkPart={props.showButtonRemoveBulkPart}
              showButtonRequoteBulkPart={showButtonRequoteBulkPart}
            />
          )
        )}
      </S.ContentBox>
    </S.ContainerBox>
  );
}

export default BoxCartUnansweredItems;
